export interface VerticalState {
  topPosition: number;
  topProgress: number;
  bottomProgress: number;
  ahead: boolean;
  entering: boolean;
  contained: boolean;
  exiting: boolean;
  behind: boolean;
  visible: boolean;
}

const verticalState = (
  element: HTMLElement,
  boundary: HTMLElement | Window = window,
  marginTop = 0,
  marginBottom = marginTop,
): VerticalState => {
  const wTop = boundary === window ? window.scrollY : (boundary as HTMLElement).scrollTop;
  const wHeight = boundary === window ? window.innerHeight : (boundary as HTMLElement).offsetHeight;
  const topPosition = getTopPosition(element, boundary);
  const topProgress = getTopProgress();
  const bottomProgress = getBottomProgress();

  return {
    topPosition,
    topProgress,
    bottomProgress,
    ahead: topProgress < 0,
    entering: topProgress > 0 && topProgress < 1 && bottomProgress < 0,
    visible: topProgress > 0 && bottomProgress < 1,
    contained: (topProgress < 1 && bottomProgress > 0) || (topProgress > 1 && bottomProgress < 0),
    exiting: topProgress > 1 && bottomProgress > 0 && bottomProgress < 1,
    behind: bottomProgress > 1,
  };

  /**
   * Position of the top border of the element depending on the viewport visibility
   * @returns {number} 0 => bottom of the viewport, 1 => top of the viewport
   */
  function getTopProgress() {
    return 1 - (topPosition - (wTop + marginTop)) / (wHeight - marginTop - marginBottom);
  }

  /**
   * Position of the bottom border of the element depending on the viewport visibility
   * @returns {number} 0 => bottom of the viewport, 1 => top of the viewport
   */
  function getBottomProgress() {
    return 1 - (topPosition + element.offsetHeight - (wTop + marginTop)) / (wHeight - marginTop - marginBottom);
  }
};

/**
 * Get top position of an element in the page
 * @returns {number} The top position in pixels
 */
const getTopPosition = (domElement: HTMLElement, boundary: HTMLElement | Window = window): number => {
  let element: HTMLElement | null = domElement;
  let top = element.offsetTop;

  while ((element = element.offsetParent as HTMLElement | null) !== null && element !== boundary) {
    top += element.offsetTop;
  }

  return top;
};

export { getTopPosition, verticalState };
